<template>
  <div id="all-events-list" class="crx">
    <!-- Global overlay for longer operations -->
    <v-overlay
      :value="ProgressLoadingFlag"
      absolute
      color="#0a0f29"
      opacity="0.6"
    >
      <div class="crx-loader">
        <span class="crx-loader__halo" aria-hidden="true"></span>
        <v-progress-circular
          indeterminate
          size="64"
          width="5"
          color="#0097d7"
        ></v-progress-circular>
        <v-icon class="crx-loader__icon" color="white" size="24"
          >mdi-ballot-outline</v-icon
        >
        <div class="crx-loader__text">Loading, please wait...</div>
      </div>
    </v-overlay>

    <!-- Ambient background (decorative only) -->
    <div class="crx-ambient" aria-hidden="true">
      <span class="crx-glow crx-glow--navy"></span>
      <span class="crx-glow crx-glow--aqua"></span>
      <span class="crx-glow crx-glow--seafoam"></span>
    </div>

    <!-- Hero -->
    <section class="crx-hero crx-reveal" style="--d: 0ms">
      <div class="crx-hero__decor" aria-hidden="true">
        <span class="crx-orb crx-orb--1"></span>
        <span class="crx-orb crx-orb--2"></span>
        <span class="crx-ring crx-ring--1"></span>
        <span class="crx-ring crx-ring--2"></span>
        <span class="crx-spark crx-spark--1"></span>
        <span class="crx-spark crx-spark--2"></span>
        <span class="crx-spark crx-spark--3"></span>
        <span class="crx-spark crx-spark--4"></span>
        <svg class="crx-hero__bars" viewBox="0 0 220 120" fill="none">
          <rect x="10" y="62" width="26" height="58" rx="6" />
          <rect x="52" y="34" width="26" height="86" rx="6" />
          <rect x="94" y="6" width="26" height="114" rx="6" />
          <rect x="136" y="46" width="26" height="74" rx="6" />
          <rect x="178" y="78" width="26" height="42" rx="6" />
        </svg>
      </div>

      <v-btn
        depressed
        class="crx-refresh"
        aria-label="Refresh"
        :loading="LoadingFlag"
        @click.prevent="refreshPageData"
      >
        <v-icon left class="crx-refresh__icon">mdi-refresh</v-icon>
        <span class="crx-refresh__text">Refresh</span>
      </v-btn>

      <div class="crx-hero__grid">
        <span class="crx-hero__icon">
          <v-icon color="white" size="34">mdi-podium</v-icon>
        </span>
        <div class="crx-hero__copy">
          <div class="crx-hero__eyebrow">
            Conference <span class="crx-dot" aria-hidden="true"></span> Election
            <em>Intelligence</em>
          </div>
          <h1 class="crx-hero__title">{{ PageTitle }}</h1>
          <span class="crx-hero__accent" aria-hidden="true"></span>
          <p class="crx-hero__desc" v-if="PageDescription">
            {{ PageDescription }}
          </p>
        </div>
      </div>
    </section>

    <!-- Selection studio -->
    <section class="crx-studio crx-reveal" style="--d: 120ms">
      <header class="crx-studio__head">
        <div class="crx-head">
          <span class="crx-head__icon crx-head__icon--navy">
            <v-icon color="white" size="22">mdi-magnify</v-icon>
          </span>
          <div>
            <h2 class="crx-head__title">Conference Selection Studio</h2>
            <p class="crx-head__desc">
              Choose a year and zone to load the conference, election and report
              details.
            </p>
          </div>
        </div>
        <span class="crx-required-note">
          <span class="text-danger">*</span> indicates required field
        </span>
      </header>

      <div class="crx-inline-loader" v-if="LoadingFlag">
        <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
      </div>

      <v-form
        ref="form1"
        v-model="valid1"
        lazy-validation
        v-on:submit.prevent="searchForm"
      >
        <div class="crx-fields">
          <div class="crx-field">
            <label class="crx-field__label">
              <span class="crx-field__icon crx-field__icon--navy">
                <v-icon size="16" color="white"
                  >mdi-calendar-month-outline</v-icon
                >
              </span>
              <span><span class="text-danger">*</span> Year</span>
            </label>
            <v-autocomplete
              :reduce="(option) => option.value"
              :items="JciYearCodeOptions"
              :rules="JciYearCodeRules"
              :loading="JciYearCodeOptionsLoading"
              v-model="JciYearCode"
              placeholder="Select year"
              dense
              clearable
              required
              outlined
            ></v-autocomplete>
          </div>
          <div class="crx-field">
            <label class="crx-field__label">
              <span class="crx-field__icon crx-field__icon--aqua">
                <v-icon size="16" color="white">mdi-map-marker-outline</v-icon>
              </span>
              <span><span class="text-danger">*</span> Zone</span>
            </label>
            <v-autocomplete
              :reduce="(option) => option.value"
              v-model="ZoneCode"
              :rules="ZoneCodeRules"
              :items="ZoneCodeOptions"
              :loading="ZoneCodeOptionsLoading"
              placeholder="Select zone"
              dense
              clearable
              required
              outlined
            ></v-autocomplete>
          </div>
        </div>

        <div class="crx-actions">
          <v-btn
            :disabled="!valid1"
            @click.prevent="getConferenceRecords"
            :loading="SearchFlag"
            depressed
            large
            class="crx-btn crx-btn--search"
          >
            <v-icon left class="crx-btn__icon">mdi-magnify</v-icon>
            Search
          </v-btn>

          <v-btn
            :disabled="!JciYearCode || !ZoneCode"
            @click.prevent="downloadConferenceReport"
            depressed
            large
            class="crx-btn crx-btn--report"
          >
            <v-icon left class="crx-btn__icon">mdi-file-pdf-box</v-icon>
            Download Conference Report
          </v-btn>

          <v-btn
            :disabled="!JciYearCode || !ZoneCode"
            @click.prevent="downloadConfidentialReport"
            depressed
            large
            class="crx-btn crx-btn--confidential"
          >
            <v-icon left class="crx-btn__icon">mdi-file-lock</v-icon>
            Download Confidential Report
          </v-btn>
        </div>
      </v-form>
    </section>

    <!-- Conference & election overview -->
    <transition name="fade-slide" mode="out-in">
      <div v-if="conference" key="conference" class="crx-overview">
        <!-- Conference details -->
        <section class="crx-panel crx-reveal" style="--d: 0ms">
          <header class="crx-panel__head">
            <div class="crx-head">
              <span class="crx-head__icon crx-head__icon--navy">
                <v-icon color="white" size="22">mdi-calendar-text</v-icon>
              </span>
              <div>
                <h2 class="crx-head__title">Conference Details</h2>
                <p class="crx-head__desc">Host, schedule and deadlines</p>
              </div>
            </div>
          </header>

          <div class="crx-date-hero">
            <span class="crx-date-hero__icon">
              <v-icon color="white" size="26">mdi-calendar-star</v-icon>
            </span>
            <div class="crx-date-hero__body">
              <div class="crx-date-hero__label">Conference Date</div>
              <div class="crx-date-hero__value">
                {{ conference.ConferenceStartDate }} -
                {{ conference.ConferenceEndDate }}
              </div>
            </div>
          </div>

          <ul class="crx-rows">
            <li class="crx-row">
              <span class="crx-row__icon"
                ><v-icon size="18" color="#3a67b1"
                  >mdi-map-marker-outline</v-icon
                ></span
              >
              <span class="crx-row__label">Zone</span>
              <span class="crx-row__value">{{ conference.ZoneName }}</span>
            </li>
            <li class="crx-row">
              <span class="crx-row__icon"
                ><v-icon size="18" color="#3a67b1"
                  >mdi-home-city-outline</v-icon
                ></span
              >
              <span class="crx-row__label">Host LO</span>
              <span class="crx-row__value">{{ conference.LomName }}</span>
            </li>
            <li class="crx-row">
              <span class="crx-row__icon"
                ><v-icon size="18" color="#3a67b1"
                  >mdi-calendar-clock</v-icon
                ></span
              >
              <span class="crx-row__label">Nomination Deadline</span>
              <span class="crx-row__value">{{
                conference.NominationEndDate
              }}</span>
            </li>
            <li class="crx-row">
              <span class="crx-row__icon"
                ><v-icon size="18" color="#3a67b1"
                  >mdi-trophy-outline</v-icon
                ></span
              >
              <span class="crx-row__label">Award Bidding</span>
              <span class="crx-row__value">
                {{ conference.AwardStartDate }} -
                {{ conference.AwardEndDate }}
              </span>
            </li>
          </ul>
        </section>

        <!-- Election command center -->
        <section
          class="crx-panel crx-panel--election crx-reveal"
          style="--d: 120ms"
        >
          <header class="crx-panel__head">
            <div class="crx-head">
              <span class="crx-head__icon crx-head__icon--aqua">
                <v-icon color="white" size="22">mdi-account-tie</v-icon>
              </span>
              <div>
                <h2 class="crx-head__title">Election Details</h2>
                <p class="crx-head__desc">Seats, vacancies and withdrawals</p>
              </div>
            </div>
          </header>

          <div class="crx-metrics">
            <div
              class="crx-metric crx-metric--navy crx-reveal"
              style="--d: 180ms"
            >
              <span class="crx-metric__icon"
                ><v-icon size="18" color="#3a67b1"
                  >mdi-seat-outline</v-icon
                ></span
              >
              <div class="crx-metric__value">
                {{ conference.ZonePresidentRequiredCount }}
              </div>
              <div class="crx-metric__label">Required ZP Count</div>
            </div>
            <div
              class="crx-metric crx-metric--aqua crx-reveal"
              style="--d: 240ms"
            >
              <span class="crx-metric__icon"
                ><v-icon size="18" color="#0097d7"
                  >mdi-account-group-outline</v-icon
                ></span
              >
              <div class="crx-metric__value">{{ conference.ZvpCount }}</div>
              <div class="crx-metric__label">Required ZVP Count</div>
            </div>
            <div
              class="crx-metric crx-metric--gold crx-reveal"
              style="--d: 300ms"
            >
              <span class="crx-metric__icon"
                ><v-icon size="18" color="#b8901c"
                  >mdi-account-arrow-left-outline</v-icon
                ></span
              >
              <div class="crx-metric__value">
                {{
                  conference.ZonePresidentRequiredCount -
                  conference.ZpNomineeCount
                }}
              </div>
              <div class="crx-metric__label">Vacancy Count for ZP</div>
            </div>
            <div
              class="crx-metric crx-metric--seafoam crx-reveal"
              style="--d: 360ms"
            >
              <span class="crx-metric__icon"
                ><v-icon size="18" color="#3d9c84"
                  >mdi-account-arrow-left-outline</v-icon
                ></span
              >
              <div class="crx-metric__value">
                {{ conference.ZvpCount - conference.ZvpNomineeCount }}
              </div>
              <div class="crx-metric__label">Vacancy Count for ZVP</div>
            </div>
          </div>

          <div class="crx-deadline">
            <span class="crx-deadline__icon">
              <v-icon color="#0a0f29" size="22">mdi-timer-sand</v-icon>
            </span>
            <div class="crx-deadline__body">
              <div class="crx-deadline__label">Withdrawal Deadline</div>
              <div class="crx-deadline__value">
                {{ conference.WithdrawalEndDate }}
              </div>
            </div>
            <div class="crx-deadline__ext">
              <span class="crx-deadline__ext-value">{{
                conference.WithdrawalExtendMaxCount
              }}</span>
              <span class="crx-deadline__ext-label"
                >Withdrawal Extensions Count</span
              >
            </div>
          </div>
        </section>
      </div>
    </transition>

    <!-- Report explorer -->
    <div class="crx-explorer" v-if="ResultFlag">
      <!-- Group 1: Candidate Nominations -->
      <section class="crx-group crx-group--navy crx-reveal" style="--d: 0ms">
        <header class="crx-group__head">
          <span class="crx-group__no">01</span>
          <span class="crx-group__icon"
            ><v-icon color="white" size="20"
              >mdi-account-multiple-outline</v-icon
            ></span
          >
          <div>
            <h2 class="crx-group__title">Candidate Nominations</h2>
            <p class="crx-group__desc">
              Zone President and Zone Vice President nominees
            </p>
          </div>
        </header>

        <!-- Nomination Lists -->
        <transition-group name="card-fade" tag="div">
          <v-row v-if="ResultFlag" key="result-1">
            <v-col cols="12" md="6">
              <div class="crx-report">
                <nomination-list
                  :key="`nomination-zp-${componentsRefreshKey}`"
                  :year="JciYearCode"
                  :zone="ZoneCode"
                  title="Zone President Nominees"
                  :designation="3"
                  :type="1"
                  :enableApplication="true"
                  :enableScore="true"
                  :enableAgeProof="true"
                />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="crx-report">
                <nomination-list
                  :key="`nomination-zvp-${componentsRefreshKey}`"
                  :year="JciYearCode"
                  :zone="ZoneCode"
                  title="Zone Vice President Nominees"
                  :designation="4"
                  :type="2"
                  :enableApplication="true"
                  :enableScore="true"
                  :enableAgeProof="true"
                />
              </div>
            </v-col>
          </v-row>
        </transition-group>
      </section>

      <!-- Group 2: Disqualification and Withdrawal -->
      <section class="crx-group crx-group--gold crx-reveal" style="--d: 80ms">
        <header class="crx-group__head">
          <span class="crx-group__no">02</span>
          <span class="crx-group__icon"
            ><v-icon color="#0a0f29" size="20"
              >mdi-account-remove-outline</v-icon
            ></span
          >
          <div>
            <h2 class="crx-group__title">Disqualification &amp; Withdrawal</h2>
            <p class="crx-group__desc">
              Candidates removed at scrutiny or withdrawn by choice
            </p>
          </div>
        </header>

        <!-- DisQualification Lists -->
        <v-row v-if="ResultFlag">
          <v-col cols="12" md="6">
            <div class="crx-report">
              <disqualified-list
                :key="`disq-zp-${componentsRefreshKey}`"
                :year="JciYearCode"
                :zone="ZoneCode"
                title="Disqualified ZP Candidates "
                :designation="3"
                :type="3"
                :enableDisqualify="true"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="crx-report">
              <disqualified-list
                :key="`disq-zvp-${componentsRefreshKey}`"
                :year="JciYearCode"
                :zone="ZoneCode"
                title="Disqualified ZVP Candidates "
                :designation="4"
                :type="4"
                :enableDisqualify="true"
              />
            </div>
          </v-col>
        </v-row>

        <!-- Withdrawal Lists -->
        <v-row v-if="ResultFlag">
          <v-col cols="12" md="6">
            <div class="crx-report">
              <withdrawal-list
                :key="`withdrawal-zp-${componentsRefreshKey}`"
                :year="JciYearCode"
                :zone="ZoneCode"
                title="Withdrawal ZP Candidates "
                :designation="3"
                :type="5"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="crx-report">
              <withdrawal-list
                :key="`withdrawal-zvp-${componentsRefreshKey}`"
                :year="JciYearCode"
                :zone="ZoneCode"
                title="Withdrawal ZVP Candidates "
                :designation="4"
                :type="6"
              />
            </div>
          </v-col>
        </v-row>
      </section>

      <!-- Group 3: Contest and Election Outcomes -->
      <section
        class="crx-group crx-group--seafoam crx-reveal"
        style="--d: 160ms"
      >
        <header class="crx-group__head">
          <span class="crx-group__no">03</span>
          <span class="crx-group__icon"
            ><v-icon color="white" size="20">mdi-trophy-outline</v-icon></span
          >
          <div>
            <h2 class="crx-group__title">Contest &amp; Election Outcomes</h2>
            <p class="crx-group__desc">
              Contested results, elected officers and round-wise votes
            </p>
          </div>
        </header>

        <!-- Contested and Loss Lists -->
        <v-row v-if="ResultFlag">
          <v-col cols="12" md="6">
            <div class="crx-report">
              <contested-loss-list
                :key="`contested-zp-${componentsRefreshKey}`"
                :year="JciYearCode"
                :zone="ZoneCode"
                title="Contested & Lost ZP Candidates "
                :designation="3"
                :type="7"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="crx-report">
              <contested-loss-list
                :key="`contested-zvp-${componentsRefreshKey}`"
                :year="JciYearCode"
                :zone="ZoneCode"
                title="Contested & Lost ZVP Candidates "
                :designation="4"
                :type="8"
              />
            </div>
          </v-col>
        </v-row>

        <!-- Elected Lists -->
        <v-row v-if="ResultFlag">
          <v-col cols="12" md="6">
            <div class="crx-report">
              <elected-list
                :key="`elected-zp-${componentsRefreshKey}`"
                :year="JciYearCode"
                :zone="ZoneCode"
                title="Elected ZP "
                :designation="3"
                :type="10"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="crx-report">
              <elected-list
                :key="`elected-zvp-${componentsRefreshKey}`"
                :year="JciYearCode"
                :zone="ZoneCode"
                title="Elected ZVP  "
                :designation="4"
                :type="11"
              />
            </div>
          </v-col>
        </v-row>

        <!--Election Result-->
        <v-row v-if="ResultFlag">
          <v-col cols="12" md="6">
            <div class="crx-report">
              <election-result-list
                :key="`election-zp-${componentsRefreshKey}`"
                :conference="this.conference.ConferenceId"
                :zone="ZoneCode"
                title="Election Results of ZP"
                :designation="3"
                :type="9"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="crx-report">
              <election-result-list
                :key="`election-zvp-${componentsRefreshKey}`"
                :conference="this.conference.ConferenceId"
                :zone="ZoneCode"
                title="Election Results of ZVP"
                :designation="4"
                :type="9"
              />
            </div>
          </v-col>
        </v-row>
      </section>

      <!-- Group 4: Voting Information -->
      <section class="crx-group crx-group--aqua crx-reveal" style="--d: 240ms">
        <header class="crx-group__head">
          <span class="crx-group__no">04</span>
          <span class="crx-group__icon"
            ><v-icon color="white" size="20">mdi-vote-outline</v-icon></span
          >
          <div>
            <h2 class="crx-group__title">Voting Information</h2>
            <p class="crx-group__desc">LO-wise voting rights for the zone</p>
          </div>
        </header>

        <!-- LO Votes List -->
        <v-row v-if="ResultFlag">
          <v-col cols="12">
            <div class="crx-report crx-report--flush">
              <lo-votes-list
                :key="`lo-votes-${componentsRefreshKey}`"
                :zone="ZoneCode"
                title="LO Information"
                :type="12"
              />
            </div>
          </v-col>
        </v-row>
      </section>
    </div>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import NominationList from "@/view/pages/erp/reports/conference/NominationList.vue";
import WithdrawalList from "@/view/pages/erp/reports/conference/WithdrawalList.vue";
import DisqualifiedList from "@/view/pages/erp/reports/conference/DisqualifiedCandidates.vue";
import contestedLossList from "@/view/pages/erp/reports/conference/ContestedLossList.vue";
import ElectionResultList from "@/view/pages/erp/reports/conference/ElectionResultList.vue";
import ElectedList from "@/view/pages/erp/reports/conference/ElectedList.vue";
import LoVotesList from "@/view/pages/erp/reports/conference/LoVotesList.vue";

export default {
  mixins: [common],
  components: {
    NominationList,
    WithdrawalList,
    DisqualifiedList,
    contestedLossList,
    ElectedList,
    ElectionResultList,
    LoVotesList,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      ProgressLoadingFlag: false,

      conference: null,
      ConferenceId: null,
      electionConducted: false,
      ResultFlag: false,
      // key used to force remount child components when zone/year changes
      componentsRefreshKey: 0,

      ExcelDownloadFlag: false,
      ExcelLoadingFlag: false,
      ExcelFileFlag: false,

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      DownloadFlag: 0,
      ExcelFileName: "",
      excel_fields: {
        "LO Id": "LomId",
        "Zone Id": "ZoneId",
        "LO Name": "LomName",
        "President Name": "LomPresidentFullName",
        "LO NAME": "LomName",
        "Mobile No": "LomPresidentMobileNo",
        "Email Id": "LomPresidentEmailid",
      },

      ConfidentialFlag: true,
      DownloadFlag: false,
      ExcelFileName: "",
      ExcelFields: {},

      recordToEdit: "",
      record: {},
      editRecordPrompt: false,
      previewRecordPrompt: false,

      selectedData: {},

      zone: {},
      lom: {},

      JciYearCodeRules: [],
      JciYearCode: "",
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      ZoneCodeRules: [],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      yearCode: "",

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",

      RoleId: "",
    };
  },
  mounted() {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      this.ProgressLoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        var RoleId = this.$session.get("RoleId");
        RoleId = RoleId == (null || undefined) ? "" : RoleId;
        this.RoleId = RoleId;

        this.pageData();
      }
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.LoadingFlag = false;
      this.ProgressLoadingFlag = false;
      this.JciYearCodeOptionsLoading = false;
    },
    RegionCodeOptions: function () {
      console.log("watch RegionCodeOptions");
      this.RegionCodeOptionsLoading = false;
      this.ProgressLoadingFlag = false;
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.LoadingFlag = false;
      this.ProgressLoadingFlag = false;
      this.ZoneCodeOptionsLoading = false;
    },
    JciYearCode: function () {
      console.log("watch JciYearCode");
      this.getZoneCodeOptions();
    },
    ZoneCode: function () {
      console.log("watch ZoneCode");
      // if results are visible and zone changed, refresh conference records and remount child lists
      if (this.ResultFlag) {
        this.getConferenceRecords();
        // increment key so child components remount
        this.componentsRefreshKey += 1;
      }
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "conference_settings",
        Action: "report_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    // code 1
    validate() {
      if (this.$refs.form1.validate()) {
        this.snackbar = true;
      }
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      this.searchForm();
    },
    editAlert(tr) {
      console.log("editAlert called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var LomId = tr.LomId;
        console.log("LomId=" + LomId);
        this.record = tr;
        this.recordToEdit = LomId;
        this.editPageDescription = "Assign 2021 LO President for " + tr.LomName;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one record to modifiy";
        this.toast("error", message);
      }
    },
    hidePreviewRecordPrompt() {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
    },
    previewData(tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    pageData() {
      console.log("refreshPageData called");
      this.getJciYearCodeOptions();
      this.getRegionCodeOptions();
    },
    getJciYearCodeOptions() {
      console.log("getJciYearCodeOptions called");
      this.LoadingFlag = true;
      this.ProgressLoadingFlag = true;
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 8,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
      // hide global overlay when options loaded via watcher JciYearCodeOptions
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      if (yearCode != "") {
        this.LoadingFlag = true;
        this.ProgressLoadingFlag = true;
        this.ZoneCodeOptionsLoading = true;
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/zone/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (yearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getConferenceRecords() {
      console.log("getConferenceRecords called");
      // show both small and global loaders
      this.LoadingFlag = true;
      this.ProgressLoadingFlag = true;
      this.SearchFlag = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/nomination-conference/show";
      var upload = {
        UserInterface: 2,
        Zone: this.ZoneCode,
      };

      const thisIns = this;
      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          var output = response.data.output;
          var flag = response.data.flag;
          var records = response.data.records;

          if (flag == 1) {
            thisIns.conference = records;
            thisIns.electionConducted =
              records.ZvpElectionConducted == 1 ? true : false;
            thisIns.ConferenceId = records.ConferenceId;
            thisIns.ResultFlag = true;
            // success - stop loaders and enable results
            thisIns.LoadingFlag = false;
            thisIns.ProgressLoadingFlag = false;
            thisIns.SearchFlag = false;
            thisIns.toast("success", output);
          } else {
            thisIns.ProgressLoadingFlag = false;
            thisIns.SearchFlag = false;
            thisIns.LoadingFlag = false;
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.LoadingFlag = false;
          thisIns.ProgressLoadingFlag = false;
          thisIns.SearchFlag = false;
        });
    },
    downloadConferenceReport() {
      console.log("downloadConferenceReport called");
      var yearCode = this.JciYearCode;
      var zoneCode = this.ZoneCode;
      console.log("yearCode=" + yearCode + ", zoneCode=" + zoneCode);

      if (yearCode && zoneCode) {
        var server_url = companyConfig.apiURL;
        var token =
          this.$session.get("token") || localStorage.getItem("token") || "";
        var add_url =
          server_url +
          "pdf/conference-report/" +
          token +
          "/" +
          yearCode +
          "/" +
          zoneCode;
        console.log({ add_url });
        window.open(add_url, "_blank");
      } else {
        var message = "";
        if (!yearCode) {
          message += "Year should not be empty. ";
        }
        if (!zoneCode) {
          message += "Zone should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    downloadConfidentialReport() {
      console.log("downloadConfidentialReport called");
      var yearCode = this.JciYearCode;
      var zoneCode = this.ZoneCode;
      console.log("yearCode=" + yearCode + ", zoneCode=" + zoneCode);

      if (yearCode && zoneCode) {
        var server_url = companyConfig.apiURL;
        var token =
          this.$session.get("token") || localStorage.getItem("token") || "";
        var add_url =
          server_url +
          "pdf/confidential-report/" +
          token +
          "/" +
          yearCode +
          "/" +
          zoneCode;
        console.log({ add_url });
        window.open(add_url, "_blank");
      } else {
        var message = "";
        if (!yearCode) {
          message += "Year should not be empty. ";
        }
        if (!zoneCode) {
          message += "Zone should not be empty. ";
        }
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    var CurrentJciYearId = this.$session.get("CurrentJciYearId");
    // CurrentJciYearId = 11;
    CurrentJciYearId =
      CurrentJciYearId == (null || undefined) ? "" : CurrentJciYearId;
    console.log({ CurrentJciYearId });
    // this.JciYearCode = CurrentJciYearId;

    this.refreshPageData();
  },
};
</script>
<style lang="scss">
#all-events-list.crx {
  /* JCI approved palette */
  --navy: #3a67b1;
  --aqua: #0097d7;
  --gold: #edbe38;
  --seafoam: #56bda3;
  --ink: #0a0f29;
  --text: #414042;
  --white: #ffffff;
  --danger: #c53030;

  --surface: #ffffff;
  --surface-tint: #f5f8fd;
  --line: #e3e9f3;
  --line-strong: #cdd8ea;
  --muted: #6b7280;

  --radius-lg: 22px;
  --radius-md: 16px;
  --radius-sm: 12px;

  --ease: cubic-bezier(0.22, 0.61, 0.36, 1);
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
  --t-fast: 180ms;
  --t-med: 280ms;

  --shadow-sm: 0 1px 2px rgba(10, 15, 41, 0.04),
    0 4px 14px rgba(58, 103, 177, 0.07);
  --shadow-md: 0 2px 4px rgba(10, 15, 41, 0.04),
    0 16px 36px rgba(58, 103, 177, 0.13);

  position: relative;
  isolation: isolate;
  padding: 4px 0 32px;
  color: var(--text);
  font-family: "Poppins", "Roboto", sans-serif;
  overflow-x: hidden;

  /* ---------- Ambient background ---------- */
  .crx-ambient {
    position: absolute;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
  }
  .crx-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.35;
    animation: crx-drift 26s ease-in-out infinite alternate;
  }
  .crx-glow--navy {
    width: 440px;
    height: 440px;
    top: 280px;
    left: -160px;
    background: rgba(58, 103, 177, 0.35);
  }
  .crx-glow--aqua {
    width: 400px;
    height: 400px;
    top: 760px;
    right: -140px;
    background: rgba(0, 151, 215, 0.25);
    animation-delay: -9s;
  }
  .crx-glow--seafoam {
    width: 320px;
    height: 320px;
    bottom: 120px;
    left: 35%;
    background: rgba(86, 189, 163, 0.2);
    animation-delay: -16s;
  }

  /* ---------- Entrance reveal ---------- */
  .crx-reveal {
    animation: crx-rise 640ms var(--ease-out) backwards;
    animation-delay: var(--d, 0ms);
  }

  /* ---------- Loader ---------- */
  .crx-loader {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    min-width: 260px;
    padding: 30px 34px 24px;
    border-radius: var(--radius-lg);
    background: linear-gradient(
      150deg,
      rgba(58, 103, 177, 0.92),
      rgba(10, 15, 41, 0.88)
    );
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow: 0 30px 60px -20px rgba(0, 0, 0, 0.55);
    overflow: hidden;
  }
  .crx-loader__halo {
    position: absolute;
    top: -60px;
    left: 50%;
    width: 180px;
    height: 180px;
    margin-left: -90px;
    border-radius: 50%;
    background: radial-gradient(rgba(0, 151, 215, 0.45), transparent 70%);
    animation: crx-breathe 2.4s ease-in-out infinite;
  }
  .crx-loader__icon {
    position: absolute !important;
    top: 50px;
    left: 50%;
    transform: translateX(-50%);
  }
  .crx-loader__text {
    position: relative;
    font-size: 0.9rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: rgba(255, 255, 255, 0.9);
  }

  /* ---------- Hero ---------- */
  .crx-hero {
    position: relative;
    overflow: hidden;
    border-radius: 28px;
    padding: 34px 40px;
    margin-bottom: 22px;
    color: var(--white);
    background: radial-gradient(
        120% 140% at 100% 0%,
        rgba(0, 151, 215, 0.75) 0%,
        rgba(0, 151, 215, 0) 55%
      ),
      radial-gradient(
        80% 120% at 0% 100%,
        rgba(86, 189, 163, 0.35) 0%,
        rgba(86, 189, 163, 0) 60%
      ),
      linear-gradient(125deg, var(--navy) 0%, var(--navy) 50%, var(--aqua) 100%);
    box-shadow: 0 24px 60px -24px rgba(58, 103, 177, 0.6);
  }
  .crx-hero::before {
    /* slow moving light sweep */
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      115deg,
      transparent 30%,
      rgba(255, 255, 255, 0.08) 45%,
      transparent 60%
    );
    background-size: 250% 100%;
    animation: crx-sweep 9s linear infinite;
    pointer-events: none;
  }
  .crx-hero::after {
    /* fine grid texture */
    content: "";
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
        rgba(255, 255, 255, 0.06) 1px,
        transparent 1px
      ),
      linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 34px 34px;
    mask-image: linear-gradient(90deg, transparent 0%, #000 75%);
    -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 75%);
    pointer-events: none;
  }
  .crx-hero__decor {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }
  .crx-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(40px);
    animation: crx-drift 18s ease-in-out infinite alternate;
  }
  .crx-orb--1 {
    width: 240px;
    height: 240px;
    right: 22%;
    bottom: -140px;
    background: rgba(86, 189, 163, 0.4);
  }
  .crx-orb--2 {
    width: 170px;
    height: 170px;
    left: 40%;
    top: -90px;
    background: rgba(237, 190, 56, 0.22);
    animation-delay: -7s;
  }
  .crx-ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.16);
  }
  .crx-ring--1 {
    width: 320px;
    height: 320px;
    right: -90px;
    top: -150px;
    border-style: dashed;
    animation: crx-spin 60s linear infinite;
  }
  .crx-ring--2 {
    width: 200px;
    height: 200px;
    right: -30px;
    top: -90px;
  }
  .crx-spark {
    position: absolute;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--white);
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.9),
      0 0 18px rgba(0, 151, 215, 0.9);
    animation: crx-twinkle 3.2s ease-in-out infinite;
  }
  .crx-spark--1 {
    left: 56%;
    top: 20%;
  }
  .crx-spark--2 {
    left: 70%;
    top: 72%;
    animation-delay: -1.1s;
  }
  .crx-spark--3 {
    left: 9%;
    top: 84%;
    width: 4px;
    height: 4px;
    animation-delay: -2s;
  }
  .crx-spark--4 {
    left: 34%;
    top: 14%;
    width: 4px;
    height: 4px;
    background: var(--gold);
    box-shadow: 0 0 12px rgba(237, 190, 56, 0.9);
    animation-delay: -0.6s;
  }
  .crx-hero__bars {
    position: absolute;
    right: 40px;
    bottom: 0;
    width: 220px;
    height: 120px;

    rect {
      fill: rgba(255, 255, 255, 0.12);
      stroke: rgba(255, 255, 255, 0.28);
      stroke-width: 1;
      transform-origin: bottom;
      transform-box: fill-box;
      animation: crx-bar 900ms var(--ease-out) backwards;
    }
    rect:nth-child(2) {
      animation-delay: 80ms;
    }
    rect:nth-child(3) {
      animation-delay: 160ms;
      fill: rgba(237, 190, 56, 0.35);
      stroke: rgba(237, 190, 56, 0.7);
    }
    rect:nth-child(4) {
      animation-delay: 240ms;
    }
    rect:nth-child(5) {
      animation-delay: 320ms;
    }
  }

  /* Transparent refresh button, right side of the header, vertically centred */
  .crx-refresh.v-btn {
    position: absolute;
    top: 50%;
    right: 22px;
    transform: translateY(-50%);
    z-index: 2;
    height: 38px;
    padding: 0 16px;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: none;
    color: var(--white) !important;
    background: rgba(255, 255, 255, 0.12) !important;
    border: 1px solid rgba(255, 255, 255, 0.4);
    backdrop-filter: blur(6px);
    transition: background-color var(--t-fast) var(--ease),
      transform var(--t-med) var(--ease), box-shadow var(--t-med) var(--ease);

    .crx-refresh__icon {
      color: var(--white) !important;
      transition: transform 500ms var(--ease);
    }
    .v-progress-circular {
      color: var(--white);
    }
    &:hover {
      background: rgba(255, 255, 255, 0.22) !important;
      transform: translateY(calc(-50% - 2px));
      box-shadow: 0 12px 24px -14px rgba(10, 15, 41, 0.6);
    }
    &:hover .crx-refresh__icon {
      transform: rotate(180deg);
    }
    &:focus-visible {
      outline: 3px solid rgba(255, 255, 255, 0.7);
      outline-offset: 2px;
    }
  }

  .crx-hero__grid {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 22px;
    max-width: calc(100% - 240px);
  }
  .crx-hero__icon {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 68px;
    height: 68px;
    border-radius: 20px;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.24),
      rgba(255, 255, 255, 0.06)
    );
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25),
      0 14px 30px -12px rgba(10, 15, 41, 0.5);
    backdrop-filter: blur(6px);
    animation: crx-pop 700ms var(--ease-out) 200ms backwards;
  }
  .crx-hero__copy {
    min-width: 0;
  }
  .crx-hero__eyebrow {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 0.74rem;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.85);
    margin-bottom: 8px;

    em {
      font-family: "Lora", Georgia, serif;
      font-style: italic;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: none;
      font-size: 1.02rem;
      color: var(--gold);
    }
  }
  .crx-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--gold);
  }
  .crx-hero__title {
    margin: 0;
    font-size: clamp(1.5rem, 1.05rem + 1.6vw, 2.4rem);
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.01em;
    color: var(--white);
    overflow-wrap: anywhere;
  }
  .crx-hero__accent {
    display: block;
    width: 92px;
    height: 3px;
    margin: 12px 0 10px;
    border-radius: 3px;
    background: linear-gradient(
      90deg,
      var(--gold),
      var(--white),
      var(--seafoam),
      var(--gold)
    );
    background-size: 300% 100%;
    transform-origin: left center;
    animation: crx-gradient 6s linear infinite,
      crx-grow 900ms var(--ease-out) 300ms both;
  }
  .crx-hero__desc {
    margin: 0;
    max-width: 640px;
    font-size: 0.95rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.88);
  }

  /* ---------- Shared section head ---------- */
  .crx-head {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }
  .crx-head__icon {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 14px;
    transition: transform var(--t-med) var(--ease);
  }
  .crx-head__icon--navy {
    background: linear-gradient(135deg, var(--navy), var(--aqua));
    box-shadow: 0 10px 22px -10px rgba(58, 103, 177, 0.8);
  }
  .crx-head__icon--aqua {
    background: linear-gradient(135deg, var(--aqua), var(--seafoam));
    box-shadow: 0 10px 22px -10px rgba(0, 151, 215, 0.8);
  }
  .crx-head__title {
    margin: 0;
    font-size: 1.12rem;
    font-weight: 700;
    color: var(--ink);
    line-height: 1.3;
  }
  .crx-head__desc {
    margin: 2px 0 0;
    font-size: 0.84rem;
    color: var(--muted);
  }

  /* ---------- Selection studio ---------- */
  .crx-studio {
    position: relative;
    padding: 24px 28px 22px;
    margin-bottom: 22px;
    border-radius: var(--radius-lg);
    background: linear-gradient(180deg, #ffffff, var(--surface-tint));
    border: 1px solid var(--line);
    box-shadow: var(--shadow-sm);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 28px;
      right: 28px;
      height: 3px;
      border-radius: 0 0 3px 3px;
      background: linear-gradient(
        90deg,
        var(--navy),
        var(--aqua),
        var(--seafoam),
        var(--gold)
      );
    }
  }
  .crx-studio__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px 20px;
    margin-bottom: 18px;
  }
  .crx-required-note {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--muted);
    padding: 6px 12px;
    border-radius: 999px;
    background: var(--white);
    border: 1px solid var(--line);
  }
  .crx-inline-loader {
    display: flex;
    justify-content: center;
    margin-bottom: 12px;
  }
  .crx-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 22px;
    max-width: 820px;
  }
  .crx-field__label {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--ink);
  }
  .crx-field__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 8px;
  }
  .crx-field__icon--navy {
    background: var(--navy);
  }
  .crx-field__icon--aqua {
    background: var(--aqua);
  }
  .crx-field {
    .v-text-field--outlined > .v-input__control > .v-input__slot {
      background: var(--white);
      border-radius: var(--radius-sm);
      transition: box-shadow var(--t-med) var(--ease);
    }
    .v-text-field--outlined fieldset {
      border-color: var(--line-strong);
      border-radius: var(--radius-sm);
      transition: border-color var(--t-fast) var(--ease);
    }
    .v-text-field--outlined:not(.v-input--is-focused):not(.error--text)
      > .v-input__control
      > .v-input__slot:hover
      fieldset {
      border-color: var(--navy);
    }
    .v-input--is-focused > .v-input__control > .v-input__slot {
      box-shadow: 0 0 0 4px rgba(0, 151, 215, 0.14);
    }
    .v-input--is-focused:not(.error--text) fieldset {
      border-color: var(--aqua);
    }
  }

  .crx-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 8px;
    padding-top: 18px;
    border-top: 1px dashed var(--line-strong);
  }
  .crx-btn.v-btn {
    height: 46px;
    padding: 0 22px;
    border-radius: 14px;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-transform: none;
    color: var(--white);
    transition: transform var(--t-med) var(--ease),
      box-shadow var(--t-med) var(--ease), filter var(--t-fast) var(--ease);

    .crx-btn__icon {
      transition: transform var(--t-med) var(--ease);
    }
    &:not(.v-btn--disabled):hover {
      transform: translateY(-2px);
      filter: brightness(1.05);
    }
    &:not(.v-btn--disabled):hover .crx-btn__icon {
      transform: translateX(-1px) scale(1.12);
    }
    &:not(.v-btn--disabled):active {
      transform: translateY(0);
    }
    &:focus-visible {
      outline: 3px solid rgba(0, 151, 215, 0.55);
      outline-offset: 2px;
    }
  }
  /* Search: Navy */
  .crx-btn--search.v-btn:not(.v-btn--disabled) {
    background: var(--navy) !important;
    color: var(--white) !important;
    box-shadow: 0 12px 26px -12px rgba(58, 103, 177, 0.9);
    min-width: 150px;

    .v-icon {
      color: var(--white) !important;
    }
    &:hover {
      box-shadow: 0 18px 32px -12px rgba(58, 103, 177, 0.9);
    }
  }
  /* Conference report: Aqua */
  .crx-btn--report.v-btn:not(.v-btn--disabled) {
    background: var(--aqua) !important;
    color: var(--white) !important;
    box-shadow: 0 12px 26px -12px rgba(0, 151, 215, 0.9);

    .v-icon {
      color: var(--white) !important;
    }
    &:hover {
      box-shadow: 0 18px 32px -12px rgba(0, 151, 215, 0.9);
    }
  }
  /* Confidential report: Gold with off-black text */
  .crx-btn--confidential.v-btn:not(.v-btn--disabled) {
    background: var(--gold) !important;
    color: var(--ink) !important;
    box-shadow: 0 12px 26px -12px rgba(237, 190, 56, 0.95);

    .v-icon {
      color: var(--ink) !important;
    }
    &:hover {
      box-shadow: 0 18px 32px -12px rgba(237, 190, 56, 0.95);
    }
  }

  /* ---------- Overview (conference + election) ---------- */
  .crx-overview {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 22px;
    margin-bottom: 30px;
  }
  .crx-panel {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    padding: 22px 24px;
    border-radius: var(--radius-lg);
    background: var(--surface);
    border: 1px solid var(--line);
    box-shadow: var(--shadow-sm);
    transition: transform var(--t-med) var(--ease),
      box-shadow var(--t-med) var(--ease), border-color var(--t-med) var(--ease);

    &:hover {
      transform: translateY(-3px);
      box-shadow: var(--shadow-md);
      border-color: var(--line-strong);
    }
    &:hover .crx-head__icon {
      transform: rotate(-6deg) scale(1.05);
    }
  }

  .crx-date-hero {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 18px;
    border-radius: var(--radius-md);
    color: var(--white);
    background: radial-gradient(
        120% 160% at 100% 0%,
        rgba(0, 151, 215, 0.6),
        transparent 60%
      ),
      linear-gradient(135deg, var(--navy), var(--ink));
    box-shadow: 0 14px 28px -16px rgba(58, 103, 177, 0.9);
  }
  .crx-date-hero__icon {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.24);
  }
  .crx-date-hero__body {
    min-width: 0;
  }
  .crx-date-hero__label {
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--gold);
  }
  .crx-date-hero__value {
    margin-top: 2px;
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .crx-rows {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .crx-row {
    display: grid;
    grid-template-columns: 34px minmax(120px, 0.9fr) minmax(0, 1.4fr);
    align-items: center;
    gap: 10px;
    padding: 11px 6px;
    border-bottom: 1px solid var(--line);
    border-radius: 10px;
    transition: background-color var(--t-fast) var(--ease);

    &:last-child {
      border-bottom: 0;
    }
    &:hover {
      background: var(--surface-tint);
    }
  }
  .crx-row__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: rgba(58, 103, 177, 0.1);
  }
  .crx-row__label {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--muted);
  }
  .crx-row__value {
    font-size: 0.93rem;
    font-weight: 600;
    color: var(--ink);
    overflow-wrap: anywhere;
  }

  /* ---------- Election command center ---------- */
  .crx-metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  .crx-metric {
    --accent: var(--navy);
    position: relative;
    overflow: hidden;
    padding: 14px 16px 14px;
    border-radius: var(--radius-md);
    background: var(--surface-tint);
    border: 1px solid var(--line);
    transition: transform var(--t-med) var(--ease),
      box-shadow var(--t-med) var(--ease), border-color var(--t-med) var(--ease);

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 14px;
      bottom: 14px;
      width: 4px;
      border-radius: 0 4px 4px 0;
      background: var(--accent);
    }
    &:hover {
      transform: translateY(-2px);
      border-color: var(--accent);
      box-shadow: 0 12px 24px -16px rgba(10, 15, 41, 0.45);
    }
    &:hover .crx-metric__icon {
      transform: scale(1.1) rotate(-6deg);
    }
  }
  .crx-metric--navy {
    --accent: var(--navy);
  }
  .crx-metric--aqua {
    --accent: var(--aqua);
  }
  .crx-metric--gold {
    --accent: var(--gold);
  }
  .crx-metric--seafoam {
    --accent: var(--seafoam);
  }
  .crx-metric__icon {
    position: absolute;
    top: 12px;
    right: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: var(--white);
    border: 1px solid var(--line);
    transition: transform var(--t-med) var(--ease);
  }
  .crx-metric__value {
    font-size: 1.9rem;
    font-weight: 700;
    line-height: 1.1;
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }
  .crx-metric__label {
    margin-top: 4px;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--muted);
  }

  .crx-deadline {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px 16px;
    padding: 14px 16px;
    border-radius: var(--radius-md);
    background: linear-gradient(
      135deg,
      rgba(237, 190, 56, 0.2),
      rgba(237, 190, 56, 0.06)
    );
    border: 1px solid rgba(237, 190, 56, 0.6);
  }
  .crx-deadline__icon {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 13px;
    background: var(--gold);
    box-shadow: 0 10px 20px -10px rgba(237, 190, 56, 0.9);

    .v-icon {
      animation: crx-sand 3.6s var(--ease) infinite;
    }
  }
  .crx-deadline__body {
    flex: 1 1 180px;
    min-width: 0;
  }
  .crx-deadline__label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #8a6a0f;
  }
  .crx-deadline__value {
    margin-top: 2px;
    font-size: 1.02rem;
    font-weight: 700;
    color: var(--ink);
    overflow-wrap: anywhere;
  }
  .crx-deadline__ext {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 12px;
    background: var(--white);
    border: 1px solid rgba(237, 190, 56, 0.55);
  }
  .crx-deadline__ext-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }
  .crx-deadline__ext-label {
    font-size: 0.76rem;
    font-weight: 500;
    line-height: 1.25;
    color: var(--muted);
    max-width: 110px;
  }

  /* ---------- Report explorer ---------- */
  .crx-group {
    --accent: var(--navy);
    --accent-soft: rgba(58, 103, 177, 0.12);
    position: relative;
    margin-bottom: 30px;
  }
  .crx-group--gold {
    --accent: var(--gold);
    --accent-soft: rgba(237, 190, 56, 0.18);
  }
  .crx-group--seafoam {
    --accent: var(--seafoam);
    --accent-soft: rgba(86, 189, 163, 0.16);
  }
  .crx-group--aqua {
    --accent: var(--aqua);
    --accent-soft: rgba(0, 151, 215, 0.12);
  }
  .crx-group__head {
    position: relative;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 0 0 14px;
    margin-bottom: 6px;

    &::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 2px;
      border-radius: 2px;
      background: linear-gradient(
        90deg,
        var(--accent) 0%,
        var(--accent-soft) 35%,
        transparent 100%
      );
    }
  }
  .crx-group__no {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1;
    color: var(--accent);
    text-shadow: 0 6px 14px var(--accent-soft);
    letter-spacing: -0.02em;
  }
  .crx-group__icon {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: var(--accent);
    box-shadow: 0 10px 20px -12px var(--accent);
  }
  .crx-group__title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--ink);
  }
  .crx-group__desc {
    margin: 2px 0 0;
    font-size: 0.82rem;
    color: var(--muted);
  }

  .crx-report {
    position: relative;
    height: 100%;
    padding: 20px 20px 16px;
    border-radius: var(--radius-lg);
    background: var(--surface);
    border: 1px solid var(--line);
    box-shadow: var(--shadow-sm);
    transition: box-shadow var(--t-med) var(--ease),
      border-color var(--t-med) var(--ease);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 22px;
      width: 56px;
      height: 3px;
      border-radius: 0 0 3px 3px;
      background: var(--accent);
      transition: width var(--t-med) var(--ease);
    }
    &:hover {
      border-color: var(--line-strong);
      box-shadow: var(--shadow-md);
    }
    &:hover::before {
      width: 110px;
    }
  }
  .crx-report--flush {
    padding: 0;
    overflow: hidden;

    &::before {
      display: none;
    }
  }
  .crx-group .row > [class*="col"] {
    animation: crx-rise 560ms var(--ease-out) backwards;
  }
  .crx-group .row > [class*="col"]:nth-child(2) {
    animation-delay: 90ms;
  }

  /* ---------- Responsive ---------- */
  @media (max-width: 1263px) {
    .crx-hero__bars {
      width: 170px;
      right: 24px;
    }
    .crx-hero__grid {
      max-width: calc(100% - 180px);
    }
  }
  @media (max-width: 959px) {
    .crx-overview {
      grid-template-columns: minmax(0, 1fr);
    }
    .crx-hero {
      padding: 28px 26px;
    }
    .crx-hero__bars {
      opacity: 0.55;
    }
    .crx-hero__grid {
      max-width: 100%;
      padding-right: 130px;
    }
  }
  @media (max-width: 599px) {
    padding-bottom: 20px;

    .crx-hero {
      padding: 22px 18px;
      border-radius: 22px;
      margin-bottom: 16px;
    }
    .crx-hero__bars,
    .crx-ring {
      display: none;
    }
    .crx-hero__grid {
      align-items: flex-start;
      gap: 14px;
      padding-right: 44px;
    }
    .crx-refresh.v-btn {
      right: 14px;
      width: 38px;
      min-width: 0 !important;
      padding: 0;

      .crx-refresh__icon {
        margin: 0 !important;
      }
    }
    .crx-refresh__text {
      display: none;
    }
    .crx-hero__icon {
      width: 52px;
      height: 52px;
      border-radius: 16px;
    }
    .crx-studio,
    .crx-panel {
      padding: 18px 16px;
    }
    .crx-studio {
      margin-bottom: 16px;
    }
    .crx-fields {
      grid-template-columns: minmax(0, 1fr);
    }
    .crx-actions {
      flex-direction: column;
    }
    .crx-btn.v-btn {
      width: 100%;
    }
    .crx-overview {
      gap: 16px;
      margin-bottom: 24px;
    }
    .crx-row {
      grid-template-columns: 34px minmax(0, 1fr);
      row-gap: 2px;
    }
    .crx-row__value {
      grid-column: 2;
    }
    .crx-metric__value {
      font-size: 1.6rem;
    }
    .crx-deadline__ext {
      width: 100%;
    }
    .crx-deadline__ext-label {
      max-width: none;
    }
    .crx-report {
      padding: 16px 12px 12px;
    }
    .crx-group__no {
      font-size: 1.6rem;
    }
  }

  /* ---------- Reduced motion ---------- */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 1ms !important;
      animation-iteration-count: 1 !important;
      animation-delay: 0ms !important;
      transition-duration: 1ms !important;
    }
    .crx-spark,
    .crx-hero::before {
      display: none;
    }
  }
}

/* Transitions (names kept from the earlier page) */
#all-events-list.crx {
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: opacity 350ms cubic-bezier(0.2, 0.8, 0.2, 1),
      transform 350ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .fade-slide-enter,
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(12px);
  }
  .card-fade-enter-active,
  .card-fade-leave-active {
    transition: opacity 300ms ease, transform 300ms ease;
  }
  .card-fade-enter,
  .card-fade-leave-to {
    opacity: 0;
    transform: translateY(8px) scale(0.995);
  }
}

/* ---------- Keyframes ---------- */
@keyframes crx-rise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes crx-pop {
  from {
    opacity: 0;
    transform: scale(0.6) rotate(-12deg);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes crx-grow {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
@keyframes crx-bar {
  from {
    transform: scaleY(0);
  }
  to {
    transform: scaleY(1);
  }
}
@keyframes crx-gradient {
  from {
    background-position: 0% 50%;
  }
  to {
    background-position: 300% 50%;
  }
}
@keyframes crx-sweep {
  from {
    background-position: 150% 0;
  }
  to {
    background-position: -100% 0;
  }
}
@keyframes crx-drift {
  0% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(30px, -20px) scale(1.06);
  }
  100% {
    transform: translate(-20px, 25px) scale(0.96);
  }
}
@keyframes crx-twinkle {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.35;
    transform: scale(0.7);
  }
}
@keyframes crx-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes crx-breathe {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}
@keyframes crx-sand {
  0%,
  70% {
    transform: rotate(0deg);
  }
  85%,
  100% {
    transform: rotate(180deg);
  }
}
</style>
